import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { domain } from '../../../Urls/Services/Api_urls';
import Swal from 'sweetalert2';
import { ModalAcs } from '../AdminAcs/ModalAcs';
import { ModalDevices } from '../AdminAcs/ModalDevices';
import { Wifi, WifiOff, EyeEmpty, EyeOff, Copy, ArrowLeft, ArrowRight, Phone } from 'iconoir-react';
import ContentLoader from '../../../Loader/ContentLoader';

const ToggleSwitch = ({ checked, disabled, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    title={label}
    disabled={disabled}
    onClick={onChange}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full p-0.5 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
      checked ? 'bg-colorprimary' : 'bg-gray-300'
    }`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
        checked ? 'translate-x-5' : 'translate-x-0'
      }`}
    />
  </button>
);

export const WifiManager = () => {
  const navigate = useNavigate();
  const [tokenACS, setTokenACS] = useState('');
  const [wifiNames, setWifiNames] = useState({});
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [devicesModalOpen, setDevicesModalOpen] = useState(false);
  const [serviceInfo, setServiceInfo] = useState({ nombre: '', planNombre: '', estado: '' });
  const [wifiPassword, setWifiPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [networkStatus, setNetworkStatus] = useState({ '24': null, '5': null });
  const [togglingBand, setTogglingBand] = useState(null);
  const [networkVisibility, setNetworkVisibility] = useState(null);
  const [togglingVisibility, setTogglingVisibility] = useState(false);
  const SN = window.sessionStorage.getItem('SN');
  const token = window.sessionStorage.getItem('token');
  const suscriptor = window.sessionStorage.getItem('Suscriptor');
  const contractedService = window.sessionStorage.getItem('ContractedService');

  useEffect(() => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
    const post = (path, body) =>
      fetch(domain + path, { method: 'POST', headers, body: JSON.stringify(body) });

    // Datos del servicio (hero): no dependen del token ACS, se resuelven en paralelo con el login
    const loadServiceInfo = async () => {
      try {
        // AdminSol ya dejó estos datos en sessionStorage; post2venta/ solo se consulta como respaldo
        const cached = JSON.parse(window.sessionStorage.getItem('WifiServiceInfo') || 'null');
        if (cached) {
          setServiceInfo(cached);
          return;
        }
        const serviceResp = await fetch(domain + '/post2venta/', { method: 'GET', headers });
        const serviceResult = await serviceResp.json();
        const items = Array.isArray(serviceResult) ? serviceResult : serviceResult?.data || [];
        const match = items.find(
          (item) => String(item?.contract?.data?.suscriptor) === String(suscriptor)
        ) || items[0];
        if (match) {
          const nombre = match.contract?.data?.nombre || '';
          const firstName = nombre.split(' ')[0] || nombre;
          setServiceInfo({
            nombre: firstName,
            planNombre: match.contract?.data?.objProductos?.[0]?.planNombre || '',
            estado: match.contract?.data?.objProductos?.[0]?.categoria_estado || '',
          });
        }
      } catch {
        // serviceInfo stays empty, hero degrades gracefully
      }
    };

    const loadVisibility = async (acsToken) => {
      try {
        const resp = await post('/post2venta/wifi/visibility/', {
          sn: SN, token: acsToken, visibility: 'view', contracted_service: contractedService,
        });
        const json = await resp.json();
        setNetworkVisibility(
          json.type === 'success' && json.data?.visibility ? json.data.visibility : 'show'
        );
      } catch {
        setNetworkVisibility('show');
      }
    };

    // Devuelve false si el SSID no está disponible (el equipo no se gestiona por este canal)
    const loadSsid = async (acsToken) => {
      try {
        const resp = await post('/post2venta/wifi/ssid/', { sn: SN, token: acsToken });
        if (!resp.ok) throw new Error('SSID no disponible');
        const json = await resp.json();
        setWifiNames(json.data || {});
        return true;
      } catch {
        return false;
      }
    };

    const loadStatus = async (acsToken) => {
      try {
        const resp = await post('/post2venta/wifi/status/', { sn: SN, token: acsToken });
        const json = await resp.json();
        if (json.type === 'success' && json.data) {
          setNetworkStatus({
            '24': json.data.wifi24 === 'ON',
            '5': json.data.wifi5 === 'ON',
          });
        }
      } catch {
        // El estado queda en null: los switches se deshabilitan hasta poder confirmarlo
      }
    };

    const loadDevices = async (acsToken) => {
      try {
        const resp = await post('/post2venta/wifi/scan/', { sn: SN, token: acsToken });
        const json = await resp.json();
        setDevices(Array.isArray(json.data) ? json.data : []);
      } catch {
        setDevices([]);
      }
    };

    const fetchData = async () => {
      setLoading(true);
      const servicePromise = loadServiceInfo();
      try {
        const loginResp = await fetch(domain + '/post2venta/wifi/acs/login/', { method: 'GET', headers });
        const loginData = await loginResp.json();
        const acsToken = loginData.token;
        setTokenACS(acsToken);

        // Con el token, todas las consultas al equipo salen a la vez; el loader se quita cuando terminan todas
        const devicesPromise = loadDevices(acsToken);
        const visibilityPromise = loadVisibility(acsToken);
        const statusPromise = loadStatus(acsToken);
        const ssidOk = await loadSsid(acsToken);

        if (!ssidOk) {
          await Swal.fire({
            icon: 'warning',
            title: 'No disponible',
            text: 'Por este canal no se puede gestionar su WiFi.',
            confirmButtonColor: '#4a1272',
          });
          navigate('/AdminSol', { replace: true });
          return;
        }
        await Promise.all([servicePromise, visibilityPromise, statusPromise, devicesPromise]);
      } catch (error) {
        console.error('Error fetching WiFi data:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No fue posible obtener la información del WiFi.',
          confirmButtonColor: '#4a1272',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleCopySSID = async (ssid) => {
    if (!ssid) return;
    try {
      await navigator.clipboard.writeText(ssid);
      Swal.fire({
        icon: 'success',
        title: '¡Copiado!',
        text: 'El nombre de red se copió al portapapeles.',
        timer: 1500,
        showConfirmButton: false,
      });
    } catch {
      Swal.fire({
        icon: 'error',
        title: 'Error al copiar',
        text: 'No fue posible copiar. Por favor, hágalo manualmente.',
        confirmButtonColor: '#4a1272',
      });
    }
  };

  const fetchPassword = async () => {
    setLoadingPassword(true);
    try {
      const resp = await fetch(domain + '/post2venta/wifi/password/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ subscriber: suscriptor, token: tokenACS }),
      });
      const json = await resp.json();
      if (json.type === 'success' && json.data?.password_ssid) {
        return json.data.password_ssid;
      }
      throw new Error(json.msg || 'Error al obtener contraseña');
    } finally {
      setLoadingPassword(false);
    }
  };

  const handleTogglePassword = async () => {
    if (showPassword) {
      setShowPassword(false);
      return;
    }
    try {
      const password = wifiPassword || (await fetchPassword());
      setWifiPassword(password);
      setShowPassword(true);
    } catch {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No fue posible obtener la contraseña. Intente nuevamente.',
        confirmButtonColor: '#4a1272',
      });
    }
  };

  const handleCopyPassword = async () => {
    try {
      const password = wifiPassword || (await fetchPassword());
      setWifiPassword(password);
      await navigator.clipboard.writeText(password);
      Swal.fire({
        icon: 'success',
        title: '¡Copiado!',
        text: 'La contraseña se copió al portapapeles.',
        timer: 1500,
        showConfirmButton: false,
      });
    } catch {
      Swal.fire({
        icon: 'error',
        title: 'Error al copiar',
        text: 'No fue posible copiar la contraseña.',
        confirmButtonColor: '#4a1272',
      });
    }
  };

  const handleToggleNetwork = async (band) => {
    if (networkStatus[band] === null || togglingBand) return;

    const previousValue = networkStatus[band];
    const newState = previousValue ? 'OFF' : 'ON';
    const bandLabel = band === '24' ? '2.4 GHz' : '5 GHz';

    if (newState === 'OFF') {
      const confirmation = await Swal.fire({
        icon: 'warning',
        title: `¿Desactivar red ${bandLabel}?`,
        text: 'Los dispositivos conectados a esta red perderán el acceso a internet.',
        showCancelButton: true,
        confirmButtonText: 'Sí, desactivar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#4a1272',
        cancelButtonColor: '#9ca3af',
        reverseButtons: true,
      });
      if (!confirmation.isConfirmed) return;
    }

    setTogglingBand(band);
    setNetworkStatus((prev) => ({ ...prev, [band]: !previousValue }));

    try {
      const resp = await fetch(domain + '/post2venta/wifi/toggle-status/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ sn: SN, token: tokenACS, band, state: newState, contracted_service: contractedService }),
      });
      const json = await resp.json();
      if (json.type !== 'success') {
        throw new Error(json.msg || 'No fue posible cambiar el estado de la red.');
      }
      Swal.fire({
        icon: 'success',
        title: newState === 'ON' ? `Red ${bandLabel} activada` : `Red ${bandLabel} desactivada`,
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      setNetworkStatus((prev) => ({ ...prev, [band]: previousValue }));
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'No fue posible cambiar el estado de la red WiFi.',
        confirmButtonColor: '#4a1272',
      });
    } finally {
      setTogglingBand(null);
    }
  };

  const handleToggleVisibility = async () => {
    if (togglingVisibility) return;

    if (networkVisibility === null) {
      Swal.fire({
        icon: 'warning',
        title: 'No disponible',
        text: 'No fue posible consultar el estado de visibilidad de la red. Recarga la página e intenta de nuevo.',
        confirmButtonColor: '#4a1272',
      });
      return;
    }

    const newVisibility = networkVisibility === 'show' ? 'hide' : 'show';

    const modalConfig =
      newVisibility === 'hide'
        ? {
            icon: 'warning',
            title: '¿Ocultar la red WiFi?',
            text: 'La red no será visible y se debera ingresar manualmente.',
            confirmButtonText: 'Sí, ocultar',
          }
        : {
            icon: 'question',
            title: '¿Mostrar la red WiFi?',
            text: 'La red volverá a ser visible para otros dispositivos.',
            confirmButtonText: 'Sí, mostrar',
          };

    const confirmation = await Swal.fire({
      ...modalConfig,
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#4a1272',
      cancelButtonColor: '#9ca3af',
      reverseButtons: true,
    });
    if (!confirmation.isConfirmed) return;

    const previousVisibility = networkVisibility;
    setTogglingVisibility(true);
    setNetworkVisibility(newVisibility);

    try {
      const resp = await fetch(domain + '/post2venta/wifi/visibility/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ sn: SN, token: tokenACS, visibility: newVisibility, contracted_service: contractedService }),
      });
      const json = await resp.json();
      if (json.type !== 'success') {
        throw new Error(json.msg || 'No fue posible cambiar la visibilidad de la red.');
      }
      Swal.fire({
        icon: 'success',
        title: newVisibility === 'show' ? 'Red visible' : 'Red ocultada',
        text: newVisibility === 'show'
          ? 'Tu red WiFi ahora es visible.'
          : 'Tu red WiFi está oculta.',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      setNetworkVisibility(previousVisibility);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'No fue posible cambiar la visibilidad de la red WiFi.',
        confirmButtonColor: '#4a1272',
      });
    } finally {
      setTogglingVisibility(false);
    }
  };

  const previewDevices = devices.slice(0, 3);
  const totalDevices = devices.length;

  if (loading || togglingVisibility) {
    return <ContentLoader />;
  }

  return (
    <>
      <div className="w-full max-w-2xl mx-auto px-2 pb-10 pt-4">
        {/* Hero Header */}
        {(() => {
          const isActive = serviceInfo.estado
            ? serviceInfo.estado.toUpperCase().includes('ACTIV')
            : true;
          return (
            <div className="bg-colorprimary text-white rounded-2xl p-4 mb-4 shadow-lg">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 flex-shrink-0">
                  {isActive
                    ? <Wifi className="w-7 h-7 text-white" />
                    : <WifiOff className="w-7 h-7 text-white" />
                  }
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isActive ? 'bg-green-400 animate-pulse' : 'bg-red-400'}`} />
                    <span className="text-white text-sm font-medium">
                      {serviceInfo.estado || (isActive ? 'Conexión activa' : 'Sin conexión')}
                    </span>
                  </div>
                  <span className="flex text-xl font-bold truncate leading-tight text-white">
                    {serviceInfo.nombre ? `WiFi de ${serviceInfo.nombre}` : ('Mi Red WiFi')}
                  </span>
                  <span className="flex text-white/80 text-sm mt-0.5 text-left">
                    {serviceInfo.planNombre || `Contrato: ${suscriptor || '—'}`}
                  </span>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Network Info Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-4">
          {/* 2.4 GHz */}
          <div className="mb-3 text-left">
            <div className="flex items-start justify-between mb-1">
              <div className="text-left">
                <span className="flex text-md font-semibold text-gray-900 leading-snug text-left">Red principal</span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-xs font-semibold text-colorprimarygreen bg-green-50 border border-green-200 rounded-full px-2.5 py-0.5">
                  2.4 GHz
                </span>
                {togglingBand === '24' ? (
                  <span className="w-4 h-4 border-2 border-colorprimary border-t-transparent rounded-full animate-spin block" />
                ) : (
                  <ToggleSwitch
                    checked={!!networkStatus['24']}
                    disabled={networkStatus['24'] === null || !!togglingBand}
                    onChange={() => handleToggleNetwork('24')}
                    label={networkStatus['24'] ? 'Desactivar red 2.4 GHz' : 'Activar red 2.4 GHz'}
                  />
                )}
              </div>
            </div>
            <span className="text-md text-gray-500 mb-1.5 ml-0.5">Nombre de red</span>
            <div className={`flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2.5 border border-gray-200 group focus-within:border-colorprimary transition-colors ${networkStatus['24'] === false ? 'opacity-50' : ''}`}>
              <span className="flex-1 text-sm font-medium text-gray-800 truncate min-w-0 select-all">
                {wifiNames?.wifi || '—'}
              </span>
              <button
                onClick={() => handleCopySSID(wifiNames?.wifi)}
                className="text-gray-400 hover:text-colorprimary transition-colors flex-shrink-0 p-0.5 rounded"
                title="Copiar nombre"
                aria-label="Copiar nombre"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            {networkStatus['24'] === false && (
              <span className="text-xs text-red-500 mt-1 ml-0.5 block">Red desactivada</span>
            )}
          </div>

          {/* 5 GHz */}
          <div className="mb-3 text-left">
            <div className="flex items-start justify-between mb-1">
              <div className="text-left">
                <span className="flex text-md font-semibold text-gray-900 leading-snug text-left">Red rápida</span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-xs font-semibold text-colorprimary bg-purple-100 border border-purple-100 rounded-full px-2.5 py-0.5">
                  5 GHz
                </span>
                {togglingBand === '5' ? (
                  <span className="w-4 h-4 border-2 border-colorprimary border-t-transparent rounded-full animate-spin block" />
                ) : (
                  <ToggleSwitch
                    checked={!!networkStatus['5']}
                    disabled={networkStatus['5'] === null || !!togglingBand}
                    onChange={() => handleToggleNetwork('5')}
                    label={networkStatus['5'] ? 'Desactivar red 5 GHz' : 'Activar red 5 GHz'}
                  />
                )}
              </div>
            </div>
            <span className="text-md text-gray-500 mt-3 mb-1.5 text-left">Nombre de red</span>
            <div className={`flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2.5 border border-gray-200 focus-within:border-colorprimary transition-colors ${networkStatus['5'] === false ? 'opacity-50' : ''}`}>
              <span className="flex-1 text-sm font-medium text-gray-800 truncate min-w-0 select-all">
                {wifiNames?.wifi5 || '—'}
              </span>
              <button
                onClick={() => handleCopySSID(wifiNames?.wifi5)}
                className="text-gray-400 hover:text-colorprimary transition-colors flex-shrink-0 p-0.5 rounded"
                title="Copiar nombre"
                aria-label="Copiar nombre"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            {networkStatus['5'] === false && (
              <span className="text-xs text-red-500 mt-1 ml-0.5 block">Red desactivada</span>
            )}
          </div>

          {/* Contraseña */}
          <div className="text-left">
            <span className="flex text-md font-semibold text-gray-900 pb-2 leading-snug text-left">
              Contraseña
            </span>
            <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2.5 border border-gray-200">
              <span className={`flex-1 text-sm font-medium text-gray-700 ${showPassword ? 'select-all' : 'tracking-[0.3em]'}`}>
                {showPassword ? wifiPassword : '••••••••••••'}
              </span>
              <button
                onClick={handleTogglePassword}
                disabled={loadingPassword}
                className="text-gray-400 hover:text-colorprimary transition-colors flex-shrink-0 p-0.5 rounded disabled:opacity-50"
                title={showPassword ? 'Ocultar contraseña' : 'Ver contraseña'}
                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              >
                {loadingPassword
                  ? <span className="w-4 h-4 border-2 border-colorprimary border-t-transparent rounded-full animate-spin block" />
                  : showPassword
                    ? <EyeOff className="w-4 h-4" />
                    : <EyeEmpty className="w-4 h-4" />
                }
              </button>
              <button
                onClick={handleCopyPassword}
                disabled={loadingPassword}
                className="text-gray-400 hover:text-colorprimary transition-colors flex-shrink-0 p-0.5 rounded disabled:opacity-50"
                title="Copiar contraseña"
                aria-label="Copiar contraseña"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Connected Devices */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-4">
          <div className="flex items-start justify-between align-items-center mb-3">
            <div className="text-left">
              <h6 className="font-semibold text-gray-800 flex items-start mb-0">
                {totalDevices > 0
                ? `Dispositivos conectados (${totalDevices})`
                : 'Dispositivos conectados (0)'}
              </h6>
            </div>
            {totalDevices > 0 && (
              <button
                onClick={() => setDevicesModalOpen(true)}
                className="flex items-center gap-1 p-0 text-colorprimary text-sm font-semibold hover:text-colorsecondary transition-colors duration-200 flex-shrink-0"
              >
                Ver todos
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>

          {previewDevices.length > 0 ? (
            <div className="space-y-2">
              {previewDevices.map((device, idx) => {
                const hostname = device.HostName || device.hostName || device.name || 'Dispositivo desconocido';
                const ip = device.HostIPAddress || device.hostIP || device.ip || '—';
                return (
                  <div
                    key={idx}
                    className="flex items-center gap-3 px-3 py-3 rounded-xl border border-gray-200 bg-gray-50"
                  >
                    <div className="bg-purple-50 border border-gray-200 text-colorprimary rounded-lg w-9 h-9 flex items-center justify-center flex-shrink-0 font-bold text-sm">
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="flex text-md font-medium text-gray-800 truncate leading-tight">{hostname}</span>
                      <span className="flex text-sm text-gray-500 truncate mt-0.5">IP: {ip}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-6 gap-2">
              <Wifi className="w-8 h-8 text-gray-300" />
              <span className="text-md text-gray-400">No se encontraron dispositivos</span>
            </div>
          )}
        </div>

        {/* Action Buttons Grid */}
        <div className="grid grid-cols-2 gap-3">
          {/* Personalizar WiFi — usa ModalAcs con cardStyle */}
          <ModalAcs sn={SN} contractedService={contractedService} tokenAcs={tokenACS} wifiNames={wifiNames} cardStyle />

          {/* Ocultar / Mostrar red */}
          <button
            onClick={handleToggleVisibility}
            disabled={togglingVisibility || networkVisibility === null}
            className="flex flex-col items-center justify-center gap-2 p-1 bg-white rounded-2xl shadow-sm border border-gray-100 hover:border-colorprimary hover:shadow-md transition-all duration-200 group min-h-[110px] disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label={networkVisibility === 'hide' ? 'Mostrar la red WiFi' : 'Ocultar la red WiFi'}
          >
            <div className="bg-purple-50 rounded-xl p-2.5 group-hover:bg-purple-100 transition-colors">
              {networkVisibility === 'hide'
                ? <EyeEmpty className="w-6 h-6 text-colorprimary" />
                : <EyeOff className="w-6 h-6 text-colorprimary" />
              }
            </div>
            <span className="text-md font-medium text-gray-700">
              {networkVisibility === 'hide' ? 'Mostrar la red' : 'Ocultar la red'}
            </span>
          </button>
          
        </div>
      </div>

      <ModalDevices
        isOpen={devicesModalOpen}
        onClose={() => setDevicesModalOpen(false)}
        devices={devices}
      />
    </>
  );
};
